import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ReactSelect from 'react-select';
import { Button, Card, CardBody, Typography } from '@material-tailwind/react';
import { FaArrowLeft, FaCalendarAlt, FaClipboardList, FaFileInvoiceDollar, FaFilter, FaSyncAlt, FaTable, FaTag } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import { formatMoney } from '../shared/formatMoney';
import ReportLink from './shared/ReportLink';
import ReportSectionActions from './shared/ReportSectionActions';
import ReportTabsBar from './shared/ReportTabsBar';
import { buildCarPath, buildOwnerStatementPath, buildOwnerTransactionPath } from './shared/reportNavigation';

const DEFAULT_RANGE = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), 0, 1);
  const format = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  return {
    date_from: format(start),
    date_to: format(today)
  };
};

const monthLabel = (value) => {
  if (!value) return '-';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? String(value).slice(0, 10)
    : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const buildMonthKeys = (fromValue, toValue) => {
  const fromDate = new Date(`${fromValue}T00:00:00`);
  const toDate = new Date(`${toValue}T00:00:00`);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime()) || fromDate > toDate) {
    return [];
  }

  const cursor = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
  const end = new Date(toDate.getFullYear(), toDate.getMonth(), 1);
  const months = [];

  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
};

const parseFilename = (contentDisposition, fallbackName) => {
  if (!contentDisposition) return fallbackName;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] || fallbackName;
};

const formatPdfDate = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const REPORT_TABS = [
  {
    id: 'zero',
    label: 'Full Zero Invoices',
    description: 'Invoices whose net amount is zero or less',
    icon: FaFileInvoiceDollar,
    fileBase: 'zero-invoices'
  },
  {
    id: 'discounted-plates',
    label: 'Discounted Plates',
    description: 'Plate rows with a discount amount',
    icon: FaClipboardList,
    fileBase: 'discounted-plates'
  },
  {
    id: 'discounted',
    label: 'Discounted Invoices',
    description: 'Invoices with at least one discounted plate',
    icon: FaTag,
    fileBase: 'discounted-invoices'
  }
];

const ZeroInvoiceReport = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const monthSummaryCaptureRef = useRef(null);
  const ownerDistributionCaptureRef = useRef(null);
  const detailedPlatesCaptureRef = useRef(null);
  const [activeTab, setActiveTab] = useState('zero');
  const [owners, setOwners] = useState([]);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_RANGE);
  const [zeroOnly, setZeroOnly] = useState(true);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token || localStorage.getItem('authToken')}` }), [token]);
  const activeReportMeta = useMemo(
    () => REPORT_TABS.find((tab) => tab.id === activeTab) || REPORT_TABS[0],
    [activeTab]
  );
  const activeReportType = activeTab === 'discounted-plates'
    ? 'discounted-plates'
    : activeTab === 'discounted'
      ? 'discounted'
      : 'zero';
  const ownerOptions = useMemo(() => (
    owners.map((owner) => ({
      value: owner.code,
      label: `${owner.code} - ${owner.name}`
    }))
  ), [owners]);

  const selectedOwnerCodes = useMemo(
    () => selectedOwners.map((item) => item.value).filter(Boolean),
    [selectedOwners]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchOwners = async () => {
      setLoadingOwners(true);
      try {
        const response = await axios.get(`${rootUrl}/owners/list`, { headers });
        const rows = Array.isArray(response.data) ? response.data : [];
        if (isMounted) {
          setOwners(rows.filter((row) => row && row.code));
        }
      } catch (err) {
        if (isMounted) {
          setOwners([]);
          setError(err.response?.data?.message || 'Failed to load owners.');
        }
      } finally {
        if (isMounted) {
          setLoadingOwners(false);
        }
      }
    };

    fetchOwners();
    return () => {
      isMounted = false;
    };
  }, [headers]);

  const reportQuery = useMemo(() => {
    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
      zero_only: zeroOnly ? 'true' : 'false',
      report_type: activeReportType
    });

    if (selectedOwnerCodes.length > 0) {
      params.set('owner_codes', selectedOwnerCodes.join(','));
    }

    return params;
  }, [activeReportType, filters.date_from, filters.date_to, selectedOwnerCodes, zeroOnly]);

  const fetchReport = async () => {
    if (!filters.date_from || !filters.date_to) {
      setError('Please choose both start and end dates.');
      return;
    }

    setLoadingReport(true);
    setError('');
    try {
      const response = await axios.get(`${rootUrl}/api/reports/monthly-zero-invoices?${reportQuery.toString()}&format=json`, {
        headers
      });
      setReport(response.data || null);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || 'Failed to load zero invoice report.';
      setError(status === 403 ? 'You do not have access to this report.' : message);
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // auto-load the active report tab on mount and when switching tabs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const exportReport = async (format, sectionSuffix = '') => {
    if (!filters.date_from || !filters.date_to) {
      setError('Please choose both start and end dates.');
      return;
    }

    setError('');

    try {
      const response = await axios.get(
        `${rootUrl}/api/reports/monthly-zero-invoices?${reportQuery.toString()}&format=${format}`,
        {
          headers,
          responseType: 'blob'
        }
      );

      const filename = parseFilename(
        response.headers?.['content-disposition'],
        `${activeReportMeta.fileBase}${sectionSuffix ? `-${sectionSuffix}` : ''}-${filters.date_from}-to-${filters.date_to}.${format === 'excel' ? 'xls' : 'pdf'}`
      );

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err.response?.data?.message || `Failed to export ${format} report.`;
      setError(message);
    }
  };

  const summary = useMemo(() => report?.summary || {}, [report?.summary]);
  const rows = useMemo(() => (Array.isArray(report?.rows) ? report.rows : []), [report?.rows]);
  const summaryByMonth = useMemo(() => (Array.isArray(report?.summary_by_month) ? report.summary_by_month : []), [report?.summary_by_month]);
  const summaryByOwner = useMemo(() => (Array.isArray(report?.summary_by_owner) ? report.summary_by_owner : []), [report?.summary_by_owner]);
  const monthKeys = buildMonthKeys(filters.date_from, filters.date_to);
  const isDiscountedPlateReport = activeTab === 'discounted-plates';

  const monthMatrix = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = row.owner_code || 'UNKNOWN';
      const period = row.invoice_period || 'UNKNOWN';
      const current = map.get(key) || {
        owner_code: row.owner_code || '-',
        owner_name: row.owner_name || '-',
        months: {},
        total: 0,
        invoice_count: new Set(),
        plate_count: new Set()
      };

      const amount = isDiscountedPlateReport ? Number(row.discount_amount || 0) : Number(row.net_amount || 0);
      current.months[period] = (current.months[period] || 0) + amount;
      current.total += amount;
      current.invoice_count.add(row.invoice_ref);
      if (row.plate_no) current.plate_count.add(row.plate_no);
      map.set(key, current);
    });

    return [...map.values()].sort((a, b) => String(a.owner_code).localeCompare(String(b.owner_code)));
  }, [rows, isDiscountedPlateReport]);

  const totalPlates = summary.total_plate_count || 0;
  const totalInvoices = summary.total_invoice_count || 0;
  const totalNet = summary.total_net_amount || 0;
  const detailedPlateTotals = useMemo(() => (
    rows.reduce((acc, row) => ({
      gross: acc.gross + Number(row.gross_amount || 0),
      discount: acc.discount + Number(row.discount_amount || 0),
      net: acc.net + Number(row.net_amount || 0)
    }), { gross: 0, discount: 0, net: 0 })
  ), [rows]);
  const ownerDistributionTotals = useMemo(() => (
    monthKeys.reduce((acc, monthKey) => {
      const monthTotal = monthMatrix.reduce((sum, row) => sum + Number(row.months[monthKey] || 0), 0);
      return {
        ...acc,
        months: {
          ...acc.months,
          [monthKey]: monthTotal
        },
        total: acc.total + monthTotal
      };
    }, { months: {}, total: 0 })
  ), [monthKeys, monthMatrix]);
  const ownersSummaryLabel = selectedOwners.length === 0
    ? 'All invoiceable owners'
    : selectedOwners.length === 1
      ? selectedOwners[0].value
      : `${selectedOwners.length} selected`;
  const pdfMetadata = [
    { label: 'Period', value: `${formatPdfDate(filters.date_from)} to ${formatPdfDate(filters.date_to)}` },
    { label: 'Owners', value: ownersSummaryLabel },
    { label: 'Report', value: activeReportMeta.label }
  ];
  return (
    <div className="min-h-screen bg-slate-100 text-slate-800">
      <div className="mx-auto max-w-[1700px] px-4 py-4 sm:py-5 lg:px-6 lg:py-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/reports/weekly')} className="bg-slate-900 text-white shadow-none hover:bg-slate-800">
              <FaArrowLeft />
              Back
            </Button>
            <div>
              <Typography className="text-lg font-bold text-slate-900">
                Zero Invoice Reports
              </Typography>
              <Typography className="text-sm text-slate-500">
                {activeReportMeta.label}
              </Typography>
            </div>
          </div>
        </div>

        <Card className="mb-4 overflow-visible shadow-lg">
          <CardBody className="bg-gradient-to-br from-[#fff8f5] to-white">
            <div className="grid gap-4 xl:grid-cols-12 xl:items-end">
              <div className="xl:col-span-2">
                <label className="mb-2 block text-sm font-medium text-blue-gray-700">
                  Date from
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((current) => ({ ...current, date_from: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
                <Typography variant="small" className="mt-1 text-xs text-slate-500">
                  Start of the reporting period.
                </Typography>
              </div>
              <div className="xl:col-span-2">
                <label className="mb-2 block text-sm font-medium text-blue-gray-700">
                  Date to
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((current) => ({ ...current, date_to: e.target.value }))}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
                <Typography variant="small" className="mt-1 text-xs text-slate-500">
                  End of the reporting period.
                </Typography>
              </div>
              <div className="xl:col-span-5">
                <label className="mb-2 block text-sm font-medium text-blue-gray-700">
                  Owners
                </label>
                <ReactSelect
                  isMulti
                  isLoading={loadingOwners}
                  options={ownerOptions}
                  value={selectedOwners}
                  onChange={(value) => setSelectedOwners(value || [])}
                  placeholder="All invoiceable owners"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '46px',
                      borderColor: state.isFocused ? '#fb923c' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 146, 60, 0.12)' : 'none',
                      borderRadius: '0.75rem'
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingTop: '2px',
                      paddingBottom: '2px'
                    }),
                    menu: (base) => ({ ...base, zIndex: 30 }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                  }}
                />
                <Typography variant="small" className="mt-1 text-xs text-slate-500">
                  Leave empty to include all invoiceable owners.
                </Typography>
              </div>
              <div className="xl:col-span-3">
                <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                  <div className="flex flex-col gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={zeroOnly}
                        onChange={(e) => setZeroOnly(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                        disabled={activeTab !== 'zero'}
                      />
                      Zero amount only
                    </label>
                    {activeTab !== 'zero' && (
                      <Typography variant="small" className="text-xs text-slate-500">
                        This filter applies only to the zero-invoice tab.
                      </Typography>
                    )}
                    <Button
                      onClick={fetchReport}
                      className="w-full bg-orange-600 shadow-none hover:bg-orange-500"
                      disabled={loadingReport}
                    >
                      <FaSyncAlt className={loadingReport ? 'animate-spin' : ''} />
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        <ReportTabsBar tabs={REPORT_TABS} activeTab={activeTab} onChange={setActiveTab} />

        {error && (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div className="mb-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'Invoices', value: totalInvoices.toLocaleString(), icon: FaFileInvoiceDollar },
              { label: 'Plates', value: totalPlates.toLocaleString(), icon: FaTable },
              { label: 'Owners', value: summaryByOwner.length.toLocaleString(), icon: FaFilter },
              { label: 'Net Amount', value: formatMoney(totalNet), icon: FaCalendarAlt }
            ].map((card) => {
              const Icon = card.icon;
              return (
                <Card key={card.label} className="shadow-lg">
                  <CardBody className="flex items-center justify-between">
                    <div>
                      <Typography variant="small" className="font-medium text-slate-500">
                        {card.label}
                      </Typography>
                      <Typography className="mt-1 text-xl font-bold text-slate-900">
                        {card.value}
                      </Typography>
                    </div>
                    <div className="rounded-2xl bg-orange-50 p-3 text-orange-600">
                      <Icon />
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>

          <Card className="shadow-lg">
            <CardBody className="p-0">
              <div className="relative flex flex-col gap-3 border-b border-slate-200 px-5 py-4 pr-44 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <Typography className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Month summary
                  </Typography>
                  <Typography className="text-sm text-slate-500">
                    Aggregated by month and owner for the selected period.
                  </Typography>
                </div>
                <div className="absolute -top-4 right-4">
                  <ReportSectionActions
                    sectionRef={monthSummaryCaptureRef}
                    fileBase={`${activeReportMeta.fileBase}-month-summary`}
                    disabled={loadingReport}
                    pdfOptions={{
                      reportTitle: 'Zero Invoice Reports',
                      sectionTitle: 'Month summary',
                      subtitle: 'Aggregated by month and owner for the selected period.',
                      metadata: pdfMetadata
                    }}
                    onExportExcel={() => exportReport('excel', 'month-summary')}
                    onExportPdf={() => exportReport('pdf', 'month-summary')}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table ref={monthSummaryCaptureRef} className="min-w-[900px] w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Month</th>
                      <th className="px-4 py-3 text-left font-semibold">Owner</th>
                      <th className="px-4 py-3 text-right font-semibold">Invoices</th>
                      <th className="px-4 py-3 text-right font-semibold">Plates</th>
                      <th className="px-4 py-3 text-right font-semibold">Gross</th>
                      <th className="px-4 py-3 text-right font-semibold">Discount</th>
                      <th className="px-4 py-3 text-right font-semibold">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByMonth.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                          No summary rows found for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      summaryByMonth.map((row) => (
                        <tr key={`${row.month}-${row.owner_code}`} className="border-t border-slate-200">
                          <td className="px-4 py-3 font-medium text-slate-900">{row.month}</td>
                          <td className="px-4 py-3">
                            <ReportLink to={buildOwnerStatementPath(row.owner_code)} className="block">
                              {row.owner_code}
                            </ReportLink>
                            <div className="text-slate-500">{row.owner_name}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{Number(row.invoice_count || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{Number(row.plate_count || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right font-semibold">{formatMoney(row.gross_amount || 0)}</td>
                          <td className="px-4 py-3 text-right font-semibold">{formatMoney(row.discount_amount || 0)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <Card className="shadow-lg">
            <CardBody className="p-0">
              <div className="relative flex flex-col gap-3 border-b border-slate-200 px-5 py-4 pr-44 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <Typography className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {isDiscountedPlateReport ? 'Owner discount distribution' : 'Owner distribution'}
                  </Typography>
                  <Typography className="text-sm text-slate-500">
                    {isDiscountedPlateReport
                      ? 'Month-by-month total discount for selected owners.'
                      : 'Month-by-month net amount for selected owners.'}
                  </Typography>
                </div>
                <div className="absolute -top-4 right-4">
                  <ReportSectionActions
                    sectionRef={ownerDistributionCaptureRef}
                    fileBase={`${activeReportMeta.fileBase}-owner-distribution`}
                    disabled={loadingReport}
                    pdfOptions={{
                      reportTitle: 'Zero Invoice Reports',
                      sectionTitle: isDiscountedPlateReport ? 'Owner discount distribution' : 'Owner distribution',
                      subtitle: isDiscountedPlateReport
                        ? 'Month-by-month total discount for selected owners.'
                        : 'Month-by-month net amount for selected owners.',
                      metadata: pdfMetadata
                    }}
                    onExportExcel={() => exportReport('excel', 'owner-distribution')}
                    onExportPdf={() => exportReport('pdf', 'owner-distribution')}
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table ref={ownerDistributionCaptureRef} className="min-w-[900px] w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Owner</th>
                      {monthKeys.map((monthKey) => (
                        <th key={monthKey} className="px-4 py-3 text-right font-semibold">
                          {monthLabel(`${monthKey}-01`)}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-right font-semibold">
                        {isDiscountedPlateReport ? 'Total discount' : 'Total'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthMatrix.length === 0 ? (
                      <tr>
                        <td colSpan={monthKeys.length + 2} className="px-4 py-10 text-center text-slate-500">
                          No owner distribution available.
                        </td>
                      </tr>
                    ) : (
                      monthMatrix.map((row) => (
                        <tr key={row.owner_code} className="border-t border-slate-200">
                          <td className="px-4 py-3">
                            <ReportLink to={buildOwnerStatementPath(row.owner_code)} className="block">
                              {row.owner_code}
                            </ReportLink>
                            <div className="text-slate-500">{row.owner_name}</div>
                          </td>
                          {monthKeys.map((monthKey) => (
                            <td key={`${row.owner_code}-${monthKey}`} className="px-4 py-3 text-right">
                              {formatMoney(row.months[monthKey] || 0)}
                            </td>
                          ))}
                          <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.total || 0)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {monthMatrix.length > 0 && (
                    <tfoot className="border-t border-slate-200 bg-slate-50">
                      <tr>
                        <td className="px-4 py-3 font-semibold text-slate-900">Total</td>
                        {monthKeys.map((monthKey) => (
                          <td key={`owner-total-${monthKey}`} className="px-4 py-3 text-right font-semibold text-slate-900">
                            {formatMoney(ownerDistributionTotals.months[monthKey] || 0)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {formatMoney(ownerDistributionTotals.total || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardBody>
          </Card>
        </div>

        <Card className="mt-6 shadow-lg">
          <CardBody className="p-0">
            <style>{`
              .detailed-plates-table.report-png-relaxed tbody tr td {
                padding-top: 18px !important;
                padding-bottom: 18px !important;
                vertical-align: top;
              }

              .detailed-plates-table.report-png-relaxed tbody tr {
                height: auto !important;
              }

              .detailed-plates-table.report-png-relaxed .report-owner-cell {
                vertical-align: top;
              }

              .detailed-plates-table.report-png-relaxed .report-owner-name {
                display: block;
                overflow: visible !important;
                white-space: normal !important;
                text-overflow: clip !important;
                word-break: break-word;
                line-height: 1.35;
              }
            `}</style>
            <div className="relative flex flex-col gap-3 border-b border-slate-200 px-5 py-4 pr-44 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <Typography className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Detailed plates
                </Typography>
                <Typography className="text-sm text-slate-500">
                  {activeReportMeta.label} rows with plate number detail.
                </Typography>
              </div>
              <div className="absolute -top-4 right-4">
                <ReportSectionActions
                  sectionRef={detailedPlatesCaptureRef}
                  fileBase={`${activeReportMeta.fileBase}-detailed-plates`}
                  disabled={loadingReport}
                  captureClassName="report-png-relaxed"
                  pdfOptions={{
                    reportTitle: 'Zero Invoice Reports',
                    sectionTitle: 'Detailed plates',
                    subtitle: `${activeReportMeta.label} rows with plate number detail.`,
                    metadata: pdfMetadata
                  }}
                  onExportExcel={() => exportReport('excel', 'detailed-plates')}
                  onExportPdf={() => exportReport('pdf', 'detailed-plates')}
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table ref={detailedPlatesCaptureRef} className="detailed-plates-table min-w-[1100px] w-full table-fixed text-xs">
                {isDiscountedPlateReport && (
                  <colgroup>
                    <col className="w-[50px]" />
                    <col className="w-[128px]" />
                    <col className="w-[88px]" />
                    <col className="w-[150px]" />
                    <col className="w-[92px]" />
                    <col className="w-[92px]" />
                    <col className="w-[92px]" />
                  </colgroup>
                )}
                <thead className="bg-slate-50 text-slate-600">
                  {isDiscountedPlateReport ? (
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold">Plate</th>
                      <th className="px-3 py-2 text-left font-semibold">Invoice</th>
                      <th className="px-2 py-2 text-left font-semibold">Month</th>
                      <th className="px-2 py-2 text-left font-semibold">Owner</th>
                      <th className="px-3 py-2 text-right font-semibold">Gross</th>
                      <th className="px-3 py-2 text-right font-semibold">Discount</th>
                      <th className="px-3 py-2 text-right font-semibold">Net</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Invoice</th>
                      <th className="px-4 py-3 text-left font-semibold">Month</th>
                      <th className="px-4 py-3 text-left font-semibold">Owner</th>
                      <th className="px-2 py-3 text-left font-semibold">Plate</th>
                      <th className="px-4 py-3 text-right font-semibold">Gross</th>
                      <th className="px-4 py-3 text-right font-semibold">Discount</th>
                      <th className="px-4 py-3 text-right font-semibold">Net</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                    {loadingReport ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                        Loading report...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                        No {activeReportType === 'discounted' ? 'discounted invoices' : activeReportType === 'discounted-plates' ? 'discounted plate rows' : 'zero amount invoices'} found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={`${row.invoice_ref}-${row.plate_no}`} className="border-t border-slate-200">
                        {isDiscountedPlateReport ? (
                          <>
                            <td className="px-2 py-2 font-semibold text-orange-700 whitespace-nowrap">
                              <ReportLink to={buildCarPath(row.plate_no)} className="block">
                                {row.plate_no}
                              </ReportLink>
                            </td>
                            <td className="px-3 py-2">
                              <ReportLink
                                to={buildOwnerTransactionPath(row.owner_code, row.invoice_ref)}
                                title={row.notes || 'No notes'}
                                className="font-mono text-slate-700"
                              >
                                {row.invoice_ref}
                              </ReportLink>
                            </td>
                            <td className="px-2 py-2 whitespace-nowrap">{row.invoice_period || '-'}</td>
                            <td className="px-2 py-2 max-w-[150px]">
                              <div className="report-owner-cell">
                              <ReportLink to={buildOwnerStatementPath(row.owner_code)} className="block">
                                {row.owner_code}
                              </ReportLink>
                              <div className="report-owner-name truncate text-slate-500">{row.owner_name}</div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{formatMoney(row.gross_amount || 0)}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{formatMoney(row.discount_amount || 0)}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3">
                              <ReportLink
                                to={buildOwnerTransactionPath(row.owner_code, row.invoice_ref)}
                                title={row.notes || 'No notes'}
                                className="font-mono text-slate-700"
                              >
                                {row.invoice_ref}
                              </ReportLink>
                            </td>
                            <td className="px-4 py-3">{row.invoice_period}</td>
                            <td className="px-4 py-3">
                              <ReportLink to={buildOwnerStatementPath(row.owner_code)} className="block">
                                {row.owner_code}
                              </ReportLink>
                              <div className="text-slate-500">{row.owner_name}</div>
                            </td>
                            <td className="px-2 py-3 font-semibold text-orange-700 whitespace-nowrap">
                              <ReportLink to={buildCarPath(row.plate_no)} className="block">
                                {row.plate_no}
                              </ReportLink>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold">{formatMoney(row.gross_amount || 0)}</td>
                            <td className="px-4 py-3 text-right font-semibold">{formatMoney(row.discount_amount || 0)}</td>
                            <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatMoney(row.net_amount || 0)}</td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td className="px-3 py-2 font-semibold text-slate-900" colSpan={4}>
                        Total
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-900">
                        {formatMoney(detailedPlateTotals.gross)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-900">
                        {formatMoney(detailedPlateTotals.discount)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-900">
                        {formatMoney(detailedPlateTotals.net)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ZeroInvoiceReport;
